/**
 * Builds the "Export Order" CSV for an admin order.
 *
 * The previous inline version had four problems worth naming, because the
 * output looked plausible enough that they were easy to miss:
 *
 *  - `totalCostOfItems` and `item.retailPrice` were named in the header but
 *    never populated: the first isn't a field on Order (it's the sum of the
 *    items) and the second was simply missing from the GraphQL selection.
 *  - Row values were built with one fewer field than the header declared. The
 *    misalignment was masked by `new Blob([arrayOfRows])`, which stringifies
 *    the array by joining on ',' and so happened to insert the missing leading
 *    comma. Correct output by accident, via two bugs cancelling out.
 *  - Every row carried the template literal's trailing newline *and* its
 *    source indentation into the file.
 *  - Nothing was escaped or null-guarded, so a company name containing a comma
 *    shifted that row's columns, and an order whose account or colour had been
 *    removed threw instead of exporting.
 */

const csvCell = value => {
  if (value === null || value === undefined) return ''
  const text = String(value)
  // Quote only when needed, and double any embedded quotes (RFC 4180).
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

const sum = (items, pick) =>
  items.reduce((total, item) => total + (Number(pick(item)) || 0), 0)

export function buildOrderCsv (order) {
  const items = (order && order.items) || []
  const account = (order && order.account) || {}
  const totalCostOfItems = sum(items, item => item.totalCost)

  const columns = [
    ['#', (item, index) => index + 1],
    ['orderNumber', () => order.orderNumber],
    ['companyName', () => account.companyName],
    ['name', () => order.name],
    ['createdAt', () => order.createdAt],
    ['totalCost', () => order.totalCost],
    ['totalCostOfItems', () => totalCostOfItems],
    ['placed', () => order.placed],
    ['paid', () => order.paid],
    ['item.id', item => (item.sku || {}).id],
    ['item.colour', item => describeColour(((item.sku || {}).options || {}).colour)],
    ['item.retailPrice', item => (item.sku || {}).retailPrice],
    ['product.id', item => ((item.sku || {}).product || {}).id],
    ['product.name', item => ((item.sku || {}).product || {}).name],
    ['product.reference', item => ((item.sku || {}).product || {}).reference],
    ['product.manufacturer', item => attribute(item, 'manufacturer')],
    ['product.type', item => attribute(item, 'type')],
    ['product.brand', item => attribute(item, 'brand')],
    ['product.range', item => attribute(item, 'range')],
    ['qty', item => item.qty],
    ['itemCost', item => item.itemCost],
    ['discountPercent', item => item.discountPercent],
    ['totalCost', item => item.totalCost]
  ]

  const rows = [columns.map(([label]) => csvCell(label)).join(',')]

  items.forEach((item, index) => {
    rows.push(columns.map(([, pick]) => csvCell(pick(item, index))).join(','))
  })

  return rows.join('\r\n') + '\r\n'
}

function attribute (item, name) {
  const attributes = ((item.sku || {}).product || {}).attributes || {}
  return attributes[name]
}

// Keeps the original export's shape: name, group and superGroup, space
// separated, with blanks where a part is missing.
function describeColour (colour) {
  const parts = colour || {}
  return [parts.name, parts.group, parts.superGroup]
    .map(part => part || '')
    .join(' ')
    .trim()
}
